/* global FB */
/**
 * Facebook Service for managing connecting and sharing
 *
 * @module lib/Social/FacebookService
 *  Ported from SavageBeast/Engineering/projects/radio/src/js/ui/service/FacebookService.js
 */
import { ApiFacebookConnect, ApiFacebookDisconnect } from '../../actions/Api';
import { removeFacebookData } from '../../actions/Social';
import { FacebookConstants } from '../../constants/Facebook';
import ApplicationError from '../../lib/error/ApplicationError';
import { getStore } from '../../store/StoreAccessor';
import Logger from '../../tools/Logger';

// Loads facebook SDK and executes fbAsyncInit
// actionToDispatchOnSdkLoad should be an action creator, the action of which is dispatched after FB.init is called on SDK load
export function loadFacebookSDK(appId, actionToDispatchOnSdkLoad = null) {
  // code that runs after SDK is loaded
  window.fbAsyncInit = () => {
    window.FB.init({
      version: FacebookConstants.VERSION,
      appId,
      xfbml: false,
      cookie: true,
      status: true,
    });

    if (actionToDispatchOnSdkLoad) {
      const store = getStore();
      store.dispatch(actionToDispatchOnSdkLoad());
    }
  };

  // Loads SDK (taken from Facebook SDK Docs)
  ((d, s, id) => {
    const element = d.getElementsByTagName(s)[0];
    const fjs = element;
    let js = element;
    if (d.getElementById(id)) return;
    js = d.createElement(s);
    js.id = id;
    js.src = '//connect.facebook.net/en_US/sdk.js';
    fjs.parentNode.insertBefore(js, fjs);
  })(document, 'script', 'facebook-jssdk');
}

function assignFacebookCredentials(store, props, actionToDispatchOnLogin = null) {
  FB.api('/' + props.userID, data => {
    const details = {
      facebookId: props.userID,
      facebookName: data.name,
      accessToken: props.accessToken,
      accessTokenExpires: props.expiresIn,
    };
    store.dispatch(ApiFacebookConnect(details));
    if (actionToDispatchOnLogin) {
      store.dispatch(actionToDispatchOnLogin());
    }
  });
}

export function facebookLogin(permissions, actionToDispatchOnLogin = null) {
  const store = getStore();
  const logger = Logger.child({ component: 'Social/FacebookService' });
  FB.getLoginStatus(response => {
    if (response.status) {
      if (response.status === FacebookConstants.CONNECTED) {
        logger.warn(
          'User tried to connect to Facebook when already connected. Reconnecting anyways.',
        );
      }
      FB.login(
        result => {
          if (result.authResponse) {
            assignFacebookCredentials(store, result.authResponse, actionToDispatchOnLogin);
          } else {
            logger.warn('Facebook login did not return credentials. Unsuccessful attempt.');
          }
        },
        { scope: permissions, return_scopes: true },
      );
    }
  });
}

export function removeFacebook() {
  FB.getLoginStatus(response => {
    const store = getStore();
    if (response.status === FacebookConstants.CONNECTED) {
      FB.logout(
        () => {
          store.dispatch(ApiFacebookDisconnect());
        },
        { accessToken: response.authResponse.accessToken },
      );
    } else {
      store.dispatch(ApiFacebookDisconnect());
    }
  });
}

export function updateFbTokenValidity(setFbTokenValidity) {
  FB.getLoginStatus(response => {
    const store = getStore();
    if (response.status === FacebookConstants.CONNECTED) {
      store.dispatch(setFbTokenValidity(true));
    } else {
      store.dispatch(setFbTokenValidity(false));
    }
  });
}

export function checkFacebookConnectStatus() {
  FB.getLoginStatus(response => {
    const store = getStore();
    const statusesToRemoveFBData = [
      FacebookConstants.NOT_AUTHORIZED,
      FacebookConstants.AUTHORIZATION_EXPIRED,
    ];
    if (statusesToRemoveFBData.includes(response.status)) {
      store.dispatch(removeFacebookData());
    } else if (response.status === FacebookConstants.CONNECTED) {
      assignFacebookCredentials(store, response.authResponse);
    }
  });
}

function handleFacebookResponse(resolve, reject, response) {
  if (response && !response.error) {
    resolve(response);
  } else {
    const errorMessage = response
      ? `${response.error.type}: ${response.error.message}`
      : 'No response from Facebook Api';
    reject(new ApplicationError(errorMessage));
  }
}

export function postToFacebookTimeline(message, shareSourcePath) {
  return new Promise((resolve, reject) => {
    FB.api(
      '/me/feed',
      'post',
      {
        message,
        link: shareSourcePath,
      },
      handleFacebookResponse.bind(null, resolve, reject),
    );
  });
}

export function openFacebookShareDialog(shareSourcePath) {
  FB.ui(
    {
      method: 'share',
      href: shareSourcePath,
      display: 'popup',
    },
    function () {},
  );
}
